import { useEffect, useState } from 'react';
import { useAuth as useOidcAuth } from 'react-oidc-context';
import { useNavigate, useSearchParams } from 'react-router';
import { Button } from '@/components/ui/button';
import { authService } from '@/lib/auth';

export function Callback() {
  const oidcAuth = useOidcAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const [errorDescription, setErrorDescription] = useState<string | null>(null);

  useEffect(() => {
    // Check for OAuth error in URL parameters
    const errorParam = searchParams.get('error');
    const errorDescriptionParam = searchParams.get('error_description');

    if (errorParam) {
      setError(errorParam);
      setErrorDescription(errorDescriptionParam);
      return;
    }

    // If user is authenticated, redirect to dashboard
    if (oidcAuth.isAuthenticated) {
      navigate('/');
      return;
    }

    // If there's an OIDC error, display it
    if (oidcAuth.error) {
      setError('oauth_failed');
      setErrorDescription(oidcAuth.error.message);
      return;
    }

    // If still loading, wait
    if (oidcAuth.isLoading) {
      return;
    }

    // Not authenticated, not loading, no error in URL - redirect to login
    navigate('/login');
  }, [oidcAuth.isAuthenticated, oidcAuth.isLoading, oidcAuth.error, navigate, searchParams]);

  const handleRetryLogin = () => {
    authService.login();
  };

  const handleGoToLogin = () => {
    navigate('/login');
  };

  // Loading state
  if (oidcAuth.isLoading && !error) {
    return (
      <div className='flex items-center justify-center min-h-screen'>
        <div className='text-center'>
          <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4'></div>
          <p>Authentifizierung wird verarbeitet...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className='flex items-center justify-center min-h-screen'>
        <div className='text-center max-w-md p-6'>
          <div className='text-destructive mb-4'>
            <svg className='h-12 w-12 mx-auto' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2}>
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z'
              />
            </svg>
          </div>
          <h1 className='text-xl font-semibold mb-2'>Anmeldung fehlgeschlagen</h1>
          <p className='text-muted-foreground mb-4'>{getErrorMessage(error, errorDescription)}</p>
          <div className='space-y-2'>
            <Button onClick={handleRetryLogin} className='w-full'>
              Erneut anmelden
            </Button>
            <Button onClick={handleGoToLogin} variant='outline' className='w-full'>
              Zurück zur Anmeldeseite
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}

function getErrorMessage(error: string, errorDescription: string | null): string {
  // Handle common Azure AD B2C error codes
  switch (error) {
    case 'redirect_uri_mismatch':
      return 'Die Anwendungskonfiguration ist fehlerhaft. Bitte kontaktieren Sie den Support.';
    case 'access_denied':
      return 'Der Zugriff wurde verweigert. Bitte versuchen Sie es erneut.';
    case 'invalid_request':
      return 'Die Anfrage war ungültig. Bitte versuchen Sie es erneut.';
    case 'server_error':
      return 'Ein Serverfehler ist aufgetreten. Bitte versuchen Sie es später erneut.';
    case 'temporarily_unavailable':
      return 'Der Dienst ist vorübergehend nicht verfügbar. Bitte versuchen Sie es später erneut.';
    case 'oauth_failed':
      return errorDescription || 'Die Anmeldung ist fehlgeschlagen. Bitte versuchen Sie es erneut.';
    default:
      if (errorDescription) {
        // Decode URL-encoded error description
        try {
          return decodeURIComponent(errorDescription.replace(/\+/g, ' '));
        } catch {
          return errorDescription;
        }
      }
      return 'Ein unbekannter Fehler ist aufgetreten. Bitte versuchen Sie es erneut.';
  }
}
